import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import EntityService from '../../services/EntityService';
import { API } from '../../shared/api/Api';

/**
 * Fetch all materials
 */
export const fetchMaterials = createAsyncThunk(
  'materials/fetchMaterials',
  async ({ token, params }, { rejectWithValue }) => {
    try {
      const response = await EntityService.getAllEnties(
        token,
        API.Materials.app_courses_materials_all,
        params
      );
      if (response.data.status === 200 && response.data.errorCode === 0) {
        return {
          items: response.data.data.items,
          currentPage: response.data.data.current_page,
          totalPages: response.data.data.last_page,
          total: response.data.data.total,
        };
      }
      return rejectWithValue(response.data.message || 'Failed to fetch materials');
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch materials');
    }
  }
);

/**
 * Fetch a single material by ID
 */
export const fetchMaterialById = createAsyncThunk(
  'materials/fetchMaterialById',
  async ({ token, materialId }, { rejectWithValue }) => {
    try {
      const response = await EntityService.getEntity(
        token,
        API.Materials.app_courses_materials_get,
        materialId
      );
      if (response.data.status === 200 && response.data.errorCode === 0) {
        return response.data.data;
      }
      return rejectWithValue(response.data.message || 'Failed to fetch material');
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch material');
    }
  }
);

/**
 * Create a new material
 */
export const createMaterial = createAsyncThunk(
  'materials/createMaterial',
  async ({ token, formData }, { rejectWithValue }) => {
    try {
      const data = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((item) => {
            data.append(`${key}[]`, item);
          });
        } else {
          data.append(key, value);
        }
      });

      const response = await EntityService.addEntity(
        token,
        API.Materials.app_courses_materials_add,
        data
      );
      if (response.data.status === 200 || response.data.status === 201) {
        return response.data.data;
      }
      return rejectWithValue(response.data.message || 'Failed to create material');
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to create material';
      const validationErrors = error.response?.data?.data || {};
      return rejectWithValue({ message: errorMessage, errors: validationErrors });
    }
  }
);

/**
 * Delete a material
 */
export const deleteMaterial = createAsyncThunk(
  'materials/deleteMaterial',
  async ({ token, materialId }, { rejectWithValue }) => {
    try {
      const response = await EntityService.deleteEntity(
        token,
        API.Materials.app_courses_materials_delete,
        materialId
      );
      if (response.data.status === 200) {
        return materialId;
      }
      return rejectWithValue(response.data.message || 'Failed to delete material');
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete material');
    }
  }
);

const initialState = {
  entities: [],
  pagination: {
    currentPage: 1,
    totalPages: 1,
    total: 0,
  },
  selectedEntity: null,
  initialFormData: null,
  listStatus: 'idle',
  detailStatus: 'idle',
  createStatus: 'idle',
  deleteStatus: 'idle',
  listError: null,
  detailError: null,
  createError: null,
  deleteError: null,
  formErrors: {},
};

const materialSlice = createSlice({
  name: 'materials',
  initialState,
  reducers: {
    clearSelectedEntity: (state) => {
      state.selectedEntity = null;
      state.initialFormData = null;
      state.detailStatus = 'idle';
      state.detailError = null;
      state.formErrors = {};
    },
    clearFormErrors: (state) => {
      state.formErrors = {};
      state.createError = null;
    },
    resetSubmissionStatus: (state) => {
      state.createStatus = 'idle';
      state.createError = null;
      state.formErrors = {};
    },
    updateFormData: (state, action) => {
      if (state.selectedEntity) {
        state.selectedEntity = {
          ...state.selectedEntity,
          ...action.payload,
        };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMaterials.pending, (state) => {
        state.listStatus = 'loading';
        state.listError = null;
      })
      .addCase(fetchMaterials.fulfilled, (state, action) => {
        state.listStatus = 'succeeded';
        state.entities = action.payload.items;
        state.pagination = {
          currentPage: action.payload.currentPage,
          totalPages: action.payload.totalPages,
          total: action.payload.total,
        };
        state.listError = null;
      })
      .addCase(fetchMaterials.rejected, (state, action) => {
        state.listStatus = 'failed';
        state.listError = action.payload;
      })
      .addCase(fetchMaterialById.pending, (state) => {
        state.detailStatus = 'loading';
        state.detailError = null;
      })
      .addCase(fetchMaterialById.fulfilled, (state, action) => {
        state.detailStatus = 'succeeded';
        state.selectedEntity = action.payload;
        state.initialFormData = { ...action.payload };
        state.detailError = null;
        state.formErrors = {};
      })
      .addCase(fetchMaterialById.rejected, (state, action) => {
        state.detailStatus = 'failed';
        state.detailError = action.payload;
      })
      .addCase(createMaterial.pending, (state) => {
        state.createStatus = 'loading';
        state.createError = null;
        state.formErrors = {};
      })
      .addCase(createMaterial.fulfilled, (state) => {
        state.createStatus = 'succeeded';
        state.createError = null;
        state.formErrors = {};
      })
      .addCase(createMaterial.rejected, (state, action) => {
        state.createStatus = 'failed';
        const errorData = action.payload;
        state.createError = errorData.message || 'Failed to create material';
        state.formErrors = errorData.errors || {};
      })
      .addCase(deleteMaterial.pending, (state) => {
        state.deleteStatus = 'loading';
        state.deleteError = null;
      })
      .addCase(deleteMaterial.fulfilled, (state, action) => {
        state.deleteStatus = 'succeeded';
        state.entities = state.entities.filter(material => material.id !== action.payload);
        state.deleteError = null;
      })
      .addCase(deleteMaterial.rejected, (state, action) => {
        state.deleteStatus = 'failed';
        state.deleteError = action.payload;
      });
  },
});

export const {
  clearSelectedEntity,
  clearFormErrors,
  resetSubmissionStatus,
  updateFormData,
} = materialSlice.actions;

export const selectMaterials = (state) => state.materials.entities;
export const selectMaterialsPagination = (state) => state.materials.pagination;
export const selectSelectedMaterial = (state) => state.materials.selectedEntity;
export const selectInitialFormData = (state) => state.materials.initialFormData;
export const selectMaterialsListStatus = (state) => state.materials.listStatus;
export const selectMaterialDetailStatus = (state) => state.materials.detailStatus;
export const selectMaterialCreateStatus = (state) => state.materials.createStatus;
export const selectMaterialDeleteStatus = (state) => state.materials.deleteStatus;
export const selectMaterialsListError = (state) => state.materials.listError;
export const selectMaterialDetailError = (state) => state.materials.detailError;
export const selectMaterialCreateError = (state) => state.materials.createError;
export const selectMaterialDeleteError = (state) => state.materials.deleteError;
export const selectMaterialFormErrors = (state) => state.materials.formErrors;

export default materialSlice.reducer;
